import { MATCH_STATUS } from '@/constants/matchStatus';

export interface LiveMinuteInput {
  status: string;
  elapsedMinutes?: number | null;
  elapsedExtraMinutes?: number | null;
  /** ISO string from `matches.elapsed_updated_at` — anchor for client extrapolation. */
  elapsedUpdatedAt?: string | null;
  /** Current wall-clock time. Passed in so the helper stays pure and testable. */
  nowMs: number;
}

const inferHalfCap = (elapsed: number): number => {
  // Infer the current half's cap from the server's last-known elapsed value
  // (not the extrapolated one) so we never jump from cap=45 to cap=90 mid-
  // tick. API caps elapsed at the half boundary, so:
  //   IN_PLAY 1H:  elapsed ∈ [1, 45] → cap 45
  //   IN_PLAY 2H:  elapsed ∈ [46, 90] → cap 90
  //   EXTRA_TIME 1H: elapsed ∈ [91, 105] → cap 105
  //   EXTRA_TIME 2H: elapsed ∈ [106, 120] → cap 120
  // API never returns IN_PLAY with elapsed > 90 (PENALTY_SHOOTOUT takes over),
  // so the > 90 branch only fires for EXTRA_TIME statuses.
  if (elapsed <= 45) return 45;
  if (elapsed <= 90) return 90;
  if (elapsed <= 105) return 105;
  return 120;
};

/**
 * Format the live-match status label. Returns "45+3'" / "37'" / "HT" / "PEN"
 * etc. — what we render in place of the static LIVE pill.
 *
 * Returns `null` when the match isn't live (caller decides what to do — e.g.
 * fall back to "LIVE" if the backend hasn't populated `elapsed_minutes` yet).
 */
export const formatLiveMinute = (input: LiveMinuteInput): string | null => {
  const { status, elapsedMinutes, elapsedExtraMinutes, elapsedUpdatedAt, nowMs } = input;

  if (status === MATCH_STATUS.PAUSED) return 'HT';
  if (status === MATCH_STATUS.PENALTY_SHOOTOUT) return 'PEN';

  if (status !== MATCH_STATUS.IN_PLAY && status !== MATCH_STATUS.EXTRA_TIME) {
    return null;
  }

  if (typeof elapsedMinutes !== 'number' || elapsedMinutes < 0) {
    // Match is live but the polling job hasn't populated elapsed yet (cold
    // start, brand new live match between polls). Caller falls back to LIVE.
    return null;
  }

  // Stoppage time reported by the server — render verbatim. We do NOT tick
  // the extra minute client-side: stoppage length is uncertain (1-7 min) and
  // every poll gives us the authoritative value within 1 min anyway.
  // Guard against API returning `extra: 0` (rare stoppage-start frame) — we
  // only show "X+Y'" when Y is actually > 0; otherwise just render "X'".
  if (typeof elapsedExtraMinutes === 'number' && elapsedExtraMinutes > 0) {
    return `${elapsedMinutes}+${elapsedExtraMinutes}'`;
  }

  // Regular play — extrapolate from the server timestamp forward, capped at
  // the half boundary so we never invent stoppage the API hasn't confirmed.
  // The cap derives from elapsedMinutes (the server's last-known value), not
  // from `status` alone, because EXTRA_TIME has two sub-phases (105 / 120).
  let displayElapsed = elapsedMinutes;
  if (elapsedUpdatedAt) {
    const anchorMs = new Date(elapsedUpdatedAt).getTime();
    if (Number.isFinite(anchorMs) && nowMs > anchorMs) {
      const ageMinutes = Math.floor((nowMs - anchorMs) / 60_000);
      displayElapsed = elapsedMinutes + ageMinutes;
    }
  }

  const cap = inferHalfCap(elapsedMinutes);
  if (displayElapsed > cap) displayElapsed = cap;

  return `${displayElapsed}'`;
};
